import { useCallback, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext, RenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table"
import { Checkbox } from "../components/ui/checkbox"
import { Input } from "../components/ui/input"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import { resolveIcon } from "./resolve-icon"
import { Loader2, Search, AlertCircle } from "lucide-react"
import type { DataTableColumn } from "@cogo/types"

const numberFormatter = new Intl.NumberFormat("en-US")

/** Controlled input with local state — dispatches onChange on every keystroke (Next.js parity) */
function EditableInput({ type, initialValue, placeholder, onChange }: {
  type: string
  initialValue: string
  placeholder?: string
  onChange: (value: string) => void
}) {
  const [val, setVal] = useState(initialValue)
  return (
    <Input
      type={type}
      className="h-8 text-[13px]"
      value={val}
      placeholder={placeholder}
      onChange={(e) => {
        setVal(e.target.value)
        onChange(e.target.value)
      }}
    />
  )
}

/** Format compact date strings like "20260306062450" → "2026/03/06 06:24:50" or ISO dates */
function formatCompactDate(raw: string): string {
  // Compact YYYYMM (6 chars) → YYYY/MM
  if (/^\d{6}$/.test(raw)) {
    return `${raw.slice(0, 4)}/${raw.slice(4, 6)}`
  }
  // Compact format: YYYYMMDDHHmmss (14 chars) or YYYYMMDD (8 chars)
  if (/^\d{8,14}$/.test(raw)) {
    const y = raw.slice(0, 4)
    const m = raw.slice(4, 6)
    const d = raw.slice(6, 8)
    const datePart = `${y}/${m}/${d}`
    // Include time if available (10+ chars: HH, 12+: HHmm, 14: HHmmss)
    if (raw.length >= 10) {
      const hh = raw.slice(8, 10)
      const mm = raw.length >= 12 ? raw.slice(10, 12) : "00"
      const ss = raw.length >= 14 ? raw.slice(12, 14) : "00"
      return `${datePart} ${hh}:${mm}:${ss}`
    }
    return datePart
  }
  // ISO format: try parsing
  const parsed = new Date(raw)
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}/${String(parsed.getMonth() + 1).padStart(2, "0")}/${String(parsed.getDate()).padStart(2, "0")}`
  }
  return raw
}

export function DataTableRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { resolveSymbol, dispatchEvent } = ctx
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  // Resolve data array
  const rawData = element.data ? resolveSymbol(element.data as string) : []
  const allItems: Record<string, unknown>[] = Array.isArray(rawData) ? rawData : []

  // Client-side pagination: slice data by pageIndex/pageSize if provided
  // When serverPaginated is true, data is already sliced by server — skip client slicing
  const serverPaginated = element.serverPaginated != null
    ? (typeof element.serverPaginated === "string" ? !!resolveSymbol(element.serverPaginated) : !!element.serverPaginated)
    : false
  const pageIndex = element.pageIndex != null
    ? Number(typeof element.pageIndex === "string" ? resolveSymbol(element.pageIndex) : element.pageIndex) || 0
    : undefined
  const pageSize = element.pageSize != null
    ? Number(typeof element.pageSize === "string" ? resolveSymbol(element.pageSize) : element.pageSize) || 20
    : undefined
  const items = (pageIndex != null && pageSize != null && !serverPaginated)
    ? allItems.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
    : allItems

  // Resolve loading state
  const isLoading = typeof element.loading === "string"
    ? !!resolveSymbol(element.loading)
    : !!element.loading

  // Resolve error state
  const errorMessage = element.error
    ? String(resolveSymbol(element.error) ?? "")
    : ""

  // Resolve edit mode
  const isEditMode = typeof element.editMode === "string"
    ? !!resolveSymbol(element.editMode)
    : !!element.editMode

  const allColumns: DataTableColumn[] = (element.columns as DataTableColumn[]) ?? []

  // Filter columns by visibleWhen expression
  // When element.selectable is true, the renderer already adds a row-selection checkbox column,
  // so skip any explicit "checkbox" type columns to avoid duplicate checkboxes.
  const columns = allColumns.filter((col) => {
    if (element.selectable && col.type === "checkbox") return false
    if (!col.visibleWhen) return true
    return !!resolveSymbol(col.visibleWhen)
  })

  // Configurable state heights from JSON (default: h-64 for loading/error, h-48 for empty)
  const stateHeight = (element.stateHeight as string) ?? "h-64"
  const emptyHeight = (element.emptyHeight as string) ?? "h-48"

  // Row click handler
  const handleRowClick = useCallback(
    (rowIndex: number, rowData: Record<string, unknown>) => {
      dispatchEvent(element.id, "rowClick", { rowIndex, rowData })
    },
    [element.id, dispatchEvent],
  )

  // Cell change handler (edit mode)
  const handleCellChange = useCallback(
    (rowIndex: number, columnKey: string, value: unknown, previousValue: unknown, rowData: Record<string, unknown>) => {
      dispatchEvent(element.id, "cellChange", { rowIndex, columnKey, value, previousValue, rowData })
    },
    [element.id, dispatchEvent],
  )

  // Selection toggle
  const toggleRow = useCallback((rowIndex: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedRows((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((_, i) => i)),
    )
  }, [items.length])

  // Alignment class
  const alignClass = (align?: string) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"

  // Resolve page offset for auto row numbering
  const resolvedPageIndex = typeof element.pageIndex === "string" ? Number(resolveSymbol(element.pageIndex)) || 0 : 0
  const resolvedPageSize = typeof element.pageSize === "string" ? Number(resolveSymbol(element.pageSize)) || 20 : 20

  // Render cell by column type
  const renderCell = (col: DataTableColumn, item: Record<string, unknown>, rowIndex: number) => {
    // Auto row number for "no" or "_rowNum" key when no data value exists
    if ((col.key === "no" || col.key === "_rowNum") && item[col.key] == null) {
      const rowNum = resolvedPageIndex * resolvedPageSize + rowIndex + 1
      return <span className={cn("text-[13px] tabular-nums", col.className)}>{rowNum}</span>
    }

    const value = item[col.key]
    const displayValue = value != null ? String(value) : ""

    // Edit mode: render editable input controls (skip rows marked for deletion)
    if (isEditMode && col.editable && item._operation !== "d") {
      const editType = col.editType ?? "input"
      const rowKey = String(item._rowKey ?? item.id ?? rowIndex)

      if (editType === "checkbox") {
        return (
          <Checkbox
            checked={!!value}
            onCheckedChange={(checked) => handleCellChange(rowIndex, col.key, checked, value, item)}
          />
        )
      }

      if (editType === "select") {
        const rawOpts = typeof col.editOptions === "string"
          ? resolveSymbol(col.editOptions)
          : col.editOptions
        const options: Array<{ label: string; value: string }> = Array.isArray(rawOpts) ? rawOpts : []
        // Use editValueKey for select value (e.g., itm_grp_id instead of itm_grp_nm)
        const selectValue = col.editValueKey
          ? String(item[col.editValueKey] ?? "")
          : displayValue
        return (
          <Select
            value={selectValue}
            onValueChange={(v) => {
              handleCellChange(rowIndex, col.editValueKey ?? col.key, v, value, item)
              // Also update the display key with the label if editValueKey is used
              if (col.editValueKey) {
                const selected = options.find((o) => o.value === v)
                if (selected) handleCellChange(rowIndex, col.key, selected.label, value, item)
              }
            }}
          >
            <SelectTrigger className="h-8 text-[13px]">
              <SelectValue placeholder={col.editPlaceholder ?? "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }

      // input or number — controlled with value+onChange (matches Next.js pattern)
      return (
        <EditableInput
          key={`${rowKey}-${col.key}`}
          type={editType === "number" ? "number" : "text"}
          initialValue={displayValue}
          placeholder={col.editPlaceholder}
          onChange={(newVal) => {
            const typed = editType === "number" ? Number(newVal) : newVal
            handleCellChange(rowIndex, col.key, typed, value, item)
          }}
        />
      )
    }

    // Apply formatting based on column format
    let formattedValue = displayValue
    if (col.format === "number" && value != null && !isNaN(Number(value))) {
      formattedValue = numberFormatter.format(Number(value))
    } else if (col.format === "date" && displayValue) {
      formattedValue = formatCompactDate(displayValue)
    }

    // Apply labelMap for ALL column types (not just badge)
    const resolvedLabelMap = typeof col.labelMap === "string"
      ? (resolveSymbol(col.labelMap) as Record<string, string> | undefined)
      : col.labelMap
    if (resolvedLabelMap) {
      formattedValue = resolvedLabelMap[displayValue] ?? resolvedLabelMap["__default__"] ?? formattedValue
    }

    // Append suffix if defined (e.g., "%" for rate columns)
    if (col.suffix && formattedValue) {
      formattedValue = formattedValue + col.suffix
    }

    switch (col.type) {
      case "text":
        return <span className={cn("text-[13px]", col.className)}>{formattedValue}</span>

      case "number":
        return <span className={cn("text-[13px] tabular-nums", col.className)}>{formattedValue}</span>

      case "date":
        return <span className={cn("text-[13px] tabular-nums", col.className)}>{formattedValue}</span>

      case "link": {
        const href = col.hrefKey ? String(item[col.hrefKey] ?? "") : displayValue
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("text-[13px] text-primary underline underline-offset-4 hover:text-primary/80", col.className)}
          >
            {formattedValue || href}
          </a>
        )
      }

      case "index": {
        const rowNum = resolvedPageIndex * resolvedPageSize + rowIndex + 1
        return <span className={cn("text-[13px] tabular-nums", col.className)}>{rowNum}</span>
      }

      case "badge": {
        const resolvedVariantMap = typeof col.variantMap === "string"
          ? (resolveSymbol(col.variantMap) as Record<string, string> | undefined)
          : col.variantMap
        const variantEntry = resolvedVariantMap?.[displayValue] ?? resolvedVariantMap?.["default"]
        // Support both flat string ("secondary") and nested object ({ variant, label }) formats
        const variant = typeof variantEntry === "object" && variantEntry !== null
          ? (variantEntry as { variant?: string }).variant ?? "default"
          : (variantEntry as string) ?? "default"
        const nestedLabel = typeof variantEntry === "object" && variantEntry !== null
          ? (variantEntry as { label?: string }).label
          : undefined
        const label = nestedLabel ?? resolvedLabelMap?.[displayValue] ?? displayValue
        return (
          <Badge
            variant={variant as "default" | "secondary" | "destructive" | "outline"}
            className={col.className}
          >
            {label || "—"}
          </Badge>
        )
      }

      case "checkbox":
        return (
          <Checkbox
            checked={selectedRows.has(rowIndex)}
            onCheckedChange={() => toggleRow(rowIndex)}
          />
        )

      case "button": {
        const IconComp = col.icon ? resolveIcon(col.icon) : undefined
        const isLink = col.buttonVariant === "link"
        const buttonLabel = col.text || displayValue
        const btnVariant = (col.buttonVariant as "ghost" | "outline" | "link" | "default") || "default"
        const btnSize = col.buttonVariant === "ghost" ? "icon" as const : "sm" as const
        return (
          <Button
            variant={btnVariant}
            size={btnSize}
            className={cn(
              col.buttonVariant === "ghost" && "h-8 w-8",
              isLink && "underline-offset-4 hover:underline",
              col.className,
            )}
            onClick={(e) => {
              e.stopPropagation()
              dispatchEvent(element.id, "cellClick", {
                rowIndex,
                columnKey: col.key,
                value,
                row: item,
              })
            }}
          >
            {IconComp && <IconComp className="h-4 w-4" />}
            {buttonLabel}
          </Button>
        )
      }

      case "icon": {
        const IconComp = col.icon ? resolveIcon(col.icon) : undefined
        return IconComp ? <IconComp className={cn("h-4 w-4", col.className)} /> : null
      }

      case "custom":
        if (col.template) {
          const childCtx = {
            ...ctx,
            itemContext: { ...ctx.itemContext, item: { ...item, _index: rowIndex } },
          }
          return (
            <RenderContext.Provider value={childCtx}>
              <ElementRenderer element={col.template} />
            </RenderContext.Provider>
          )
        }
        return <span>{formattedValue}</span>

      case "actions": {
        // Actions column: renders a list of button actions per row
        const actions = (col as any).actions as Array<{
          id: string; text?: string; variant?: string; size?: string;
          className?: string; icon?: string;
          events?: Record<string, { action: string; params?: Record<string, unknown> }>;
        }> | undefined
        if (!actions?.length) return null
        return (
          <div className="flex items-center justify-end gap-1">
            {actions.map((act) => {
              const ActIcon = act.icon ? resolveIcon(act.icon) : undefined
              const actVariant = (act.variant as "outline" | "ghost" | "default") || "default"
              return (
                <Button
                  key={act.id}
                  variant={actVariant}
                  size="sm"
                  className={cn(
                    act.size === "sm" && "h-7 text-xs",
                    act.className,
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    // Dispatch the action's click event with row context
                    if (act.events?.click) {
                      const flowName = act.events.click.params?.flowName as string
                      if (flowName) {
                        dispatchEvent(element.id, "actionClick", {
                          flowName,
                          row: item,
                          rowIndex,
                          actionId: act.id,
                        })
                      }
                    }
                  }}
                >
                  {ActIcon && <ActIcon className="h-3.5 w-3.5" />}
                  {act.text}
                </Button>
              )
            })}
          </div>
        )
      }

      default:
        // Auto-render as badge if variantMap is present (even without type: "badge")
        const autoVariantMap = typeof col.variantMap === "string"
          ? (resolveSymbol(col.variantMap) as Record<string, string> | undefined)
          : col.variantMap
        if (autoVariantMap) {
          const autoEntry = autoVariantMap[displayValue] ?? autoVariantMap["default"]
          const autoVariant = typeof autoEntry === "object" && autoEntry !== null
            ? (autoEntry as { variant?: string }).variant ?? "default"
            : (autoEntry as string) ?? "default"
          const autoLabel = typeof autoEntry === "object" && autoEntry !== null
            ? (autoEntry as { label?: string }).label
            : undefined
          return (
            <Badge
              variant={autoVariant as "default" | "secondary" | "destructive" | "outline"}
              className={col.className}
            >
              {autoLabel ?? (formattedValue || "—")}
            </Badge>
          )
        }
        return <span>{formattedValue}</span>
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card", stripPresetConflicts(element.className))}
        data-testid={`data-table-${element.id}`}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(stateHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="text-[13px] text-muted-foreground italic">Loading...</span>
                </div>
              </TableCell>
            </TableRow>
          </TableHeader>
        </Table>
      </div>
    )
  }

  // Error state
  if (errorMessage) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card", stripPresetConflicts(element.className))}
        data-testid={`data-table-${element.id}`}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(stateHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-2 text-destructive">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <span className="text-sm font-medium">{errorMessage}</span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    )
  }

  // Empty state — still render headers for structural parity with Next.js
  if (items.length === 0) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card", stripPresetConflicts(element.className))}
        data-testid={`data-table-${element.id}`}
      >
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {element.selectable && (
                <TableHead className="w-10">
                  <Checkbox checked={false} disabled />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    // shadcn TableHead defaults (font-medium) apply automatically
                    alignClass(col.align),
                    col.headerClassName,
                  )}
                  style={col.width ? { width: col.width } : undefined}
                  data-column={col.header ?? col.label ?? col.key}
                >
                  {col.header ?? col.label ?? ""}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(emptyHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <Search className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <span className="text-[13px] font-medium text-muted-foreground">
                    {element.emptyTitle ?? "No results"}
                  </span>
                  {element.emptySub && (
                    <span className="text-[12px] text-muted-foreground/70">
                      {element.emptySub}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    )
  }

  // Data table
  return (
    <div
      className={cn("w-full overflow-auto rounded-lg border border-border bg-card", stripPresetConflicts(element.className))}
      data-testid={`data-table-${element.id}`}
    >
      <Table>
        <TableHeader
          className={cn(
            element.stickyHeader && "sticky top-0 z-10 bg-muted/80 backdrop-blur-sm",
            element.headerClassName as string | undefined,
          )}
        >
          <TableRow className="hover:bg-transparent">
            {element.selectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedRows.size === items.length && items.length > 0}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
            )}
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  // shadcn TableHead defaults (font-medium) apply automatically
                  alignClass(col.align),
                  col.headerClassName,
                )}
                style={col.width ? { width: col.width } : undefined}
                data-column={col.header ?? col.label ?? col.key}
              >
                {col.header ?? col.label ?? ""}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody
          className={cn(
            element.striped && "striped [&>tr:nth-child(even)]:bg-muted/30",
            element.bodyClassName as string | undefined,
          )}
        >
          {items.map((item, rowIndex) => (
            <TableRow
              key={`${String(item.id ?? '')}-${rowIndex}`}
              className={cn(
                // shadcn TableRow defaults (hover:bg-muted/50) apply automatically
                selectedRows.has(rowIndex) && "bg-muted",
                isEditMode && item._operation === "d" && "opacity-50 line-through bg-destructive/10",
                isEditMode && item._operation === "c" && "bg-primary/5",
                element.rowClassName as string | undefined,
              )}
              data-state={selectedRows.has(rowIndex) ? "selected" : undefined}
              onClick={() => handleRowClick(rowIndex, item)}
            >
              {element.selectable && (
                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedRows.has(rowIndex)}
                    onCheckedChange={() => toggleRow(rowIndex)}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={alignClass(col.align)}
                  data-column={col.header ?? col.label ?? col.key}
                  data-row-index={rowIndex}
                >
                  {renderCell(col, item, rowIndex)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
